import React from "react";
import ReactDOM from "react-dom/client";
import { transform } from "@babel/standalone";

declare global {
  interface Window {
    SANDBOX_VERSION: string;
    SANDBOX_BUILD_TIME: string;
  }
}

const SANDBOX_VERSION = "1.0.0";
const SANDBOX_BUILD_TIME = new Date().toISOString();

window.SANDBOX_VERSION = SANDBOX_VERSION;
window.SANDBOX_BUILD_TIME = SANDBOX_BUILD_TIME;

// Keep in sync with app/utils/custom-view/schema.ts ResolvedAttachment.
interface ResolvedAttachment {
  reference: {
    type: "braintrust_attachment" | "external_attachment" | "inline_attachment";
    filename: string;
    content_type: string;
    key?: string;
    url?: string;
    src?: string;
  };
  downloadUrl?: string;
  embedUrl?: string;
  contentLength?: number;
}

interface CustomRendererSpan {
  id: string;
  span_id: string;
  root_span_id: string;
  parent_span_id?: string | null;
  span_parents?: string[];
  data: {
    input?: unknown;
    output?: unknown;
    expected?: unknown;
    metadata?: unknown;
    scores?: Record<string, number | null>;
    metrics?: Record<string, number | string>;
    error?: string;
    tags?: string[];
    span_attributes?: Record<string, unknown>;
    [key: string]: unknown;
  };
  children: string[];
  attachments?: ResolvedAttachment[];
}

type SpanFieldName = "input" | "output" | "expected" | "metadata";

interface SpanFields {
  input?: unknown;
  output?: unknown;
  expected?: unknown;
  metadata?: Record<string, unknown>;
}

interface CustomRendererFullTrace {
  rootSpanId: string;
  selectedSpanId: string;
  spanOrder: string[];
  spans: Record<string, CustomRendererSpan>;
  fetchSpanFields: (
    spanIds: string | string[],
    fields?: SpanFieldName[],
  ) => Promise<Record<string, SpanFields>>;
  update: (
    update: CustomViewTraceUpdate,
  ) => Promise<{ transactionId: string | null }>;
}

type CustomViewTraceUpdateTarget = "selected" | "root" | { spanId: string };

interface CustomViewTraceUpdate {
  target?: CustomViewTraceUpdateTarget;
  metadata?: Record<string, unknown>;
  scores?: Record<string, number | null>;
  tags?: string[] | null;
}

interface CustomViewDatasetUpdate {
  metadata?: Record<string, unknown>;
  input?: unknown;
  expected?: unknown;
  tags?: string[] | null;
}

interface CustomTraceRendererProps {
  trace: CustomRendererFullTrace;
  span: CustomRendererSpan;
  update: (field: string, value: unknown) => void;
  selectSpan: (spanId: string) => void;
}

// Generic props for non-trace views
type GenericCustomViewProps = Record<string, unknown>;

type ParentMessage =
  | {
      type: "load-code";
      code: string;
      trace?: Omit<CustomRendererFullTrace, "fetchSpanFields" | "update">;
      span?: CustomRendererSpan;
      [key: string]: unknown;
    }
  | {
      type: "update-code";
      code: string;
      trace?: Omit<CustomRendererFullTrace, "fetchSpanFields" | "update">;
      span?: CustomRendererSpan;
      [key: string]: unknown;
    }
  | {
      type: "data";
      trace?: Omit<CustomRendererFullTrace, "fetchSpanFields" | "update">;
      span?: CustomRendererSpan;
      [key: string]: unknown;
    }
  | { type: "ping" }
  | {
      type: "span-fields-response";
      requestId: string;
      data: Record<string, SpanFields>;
      error?: string;
    }
  | {
      type: "update-span-response";
      requestId: string;
      data?: { transactionId: string | null };
      error?: string;
    }
  | {
      type: "update-row-response";
      requestId: string;
      data?: { transactionId: string | null };
      error?: string;
    };

type SandboxMessage =
  | { type: "ready" }
  | { type: "error"; error: string; stack?: string }
  | { type: "update-metadata"; field: string; value: unknown }
  | { type: "select-span"; spanId: string }
  | { type: "pong"; version: string; buildTime: string }
  | {
      type: "fetch-span-fields";
      requestId: string;
      spanIds: string[];
      fields?: SpanFieldName[];
    }
  | {
      type: "update-span";
      requestId: string;
      update: CustomViewTraceUpdate;
    }
  | {
      type: "update-row";
      requestId: string;
      update: CustomViewDatasetUpdate;
    };

type SandboxState = {
  component:
    | React.ComponentType<CustomTraceRendererProps>
    | React.ComponentType<GenericCustomViewProps>
    | null;
  trace: Omit<CustomRendererFullTrace, "fetchSpanFields" | "update"> | null;
  span: CustomRendererSpan | null;
  data: Record<string, unknown> | null;
  code: string | null;
};

type TraceSandboxState = SandboxState & {
  component: React.ComponentType<CustomTraceRendererProps>;
  trace: Omit<CustomRendererFullTrace, "fetchSpanFields" | "update">;
  span: CustomRendererSpan;
  data: null;
};

type GenericSandboxState = SandboxState & {
  component: React.ComponentType<GenericCustomViewProps>;
  trace: null;
  span: null;
  data: Record<string, unknown>;
};

const state: SandboxState = {
  component: null,
  trace: null,
  span: null,
  data: null,
  code: null,
};

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}
const root = ReactDOM.createRoot(rootElement);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isError = (error: unknown): error is Error => error instanceof Error;

class CustomViewUpdateCancelledError extends Error {
  constructor() {
    super(
      "Update request cancelled because the custom view reloaded or data changed.",
    );
    this.name = "CustomViewUpdateCancelledError";
  }
}

function hasOwn(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function isTraceSandboxState(value: SandboxState): value is TraceSandboxState {
  return Boolean(value.component && value.trace && value.span);
}

function isGenericSandboxState(
  value: SandboxState,
): value is GenericSandboxState {
  return Boolean(value.component && value.data);
}

function normalizeMetadata(metadata: unknown): Record<string, unknown> {
  if (!isRecord(metadata)) {
    return {};
  }
  return { ...metadata };
}

function getRowId(row: Record<string, unknown> | null): string | null {
  return typeof row?.id === "string" ? row.id : null;
}

let trustedParentOrigin: string | null = null;

function postMessage(message: SandboxMessage): void {
  const canUseWildcard = message.type === "ready" || message.type === "error";
  const targetOrigin = canUseWildcard ? "*" : trustedParentOrigin;
  if (!targetOrigin) {
    return;
  }
  window.parent.postMessage(message, targetOrigin);
}

// Pending requests for fetchSpanFields
const pendingRequests = new Map<
  string,
  {
    resolve: (data: Record<string, SpanFields>) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }
>();

const pendingUpdateRequests = new Map<
  string,
  {
    resolve: (data: { transactionId: string | null }) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }
>();

// Cache for fetched span fields (span ID → field key → fields)
const spanFieldsCache = new Map<string, Map<string, SpanFields>>();

function getCacheKey(fields?: SpanFieldName[]): string {
  return fields ? [...fields].sort().join(",") : "all";
}

function clearSpanFieldsCache(): void {
  spanFieldsCache.clear();
}

function clearCachedSpanFieldsForSpan(span: CustomRendererSpan): void {
  spanFieldsCache.delete(span.id);
  spanFieldsCache.delete(span.span_id);
}

function clearPendingRequests(): void {
  for (const [, pending] of pendingRequests) {
    clearTimeout(pending.timeoutId);
  }
  pendingRequests.clear();

  for (const [, pending] of pendingUpdateRequests) {
    clearTimeout(pending.timeoutId);
    pending.reject(new CustomViewUpdateCancelledError());
  }
  pendingUpdateRequests.clear();
}

async function fetchSpanFields(
  spanIds: string | string[],
  fields?: SpanFieldName[],
): Promise<Record<string, SpanFields>> {
  const ids = Array.isArray(spanIds) ? spanIds : [spanIds];
  const cacheKey = getCacheKey(fields);

  const uncached = ids.filter((id) => {
    const fieldCache = spanFieldsCache.get(id);
    return !fieldCache || !fieldCache.has(cacheKey);
  });

  if (uncached.length === 0) {
    return Promise.resolve(
      Object.fromEntries(
        ids.map((id) => [id, spanFieldsCache.get(id)?.get(cacheKey) ?? {}]),
      ),
    );
  }

  const requestId = crypto.randomUUID();
  return new Promise<Record<string, SpanFields>>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (pendingRequests.has(requestId)) {
        pendingRequests.delete(requestId);
        reject(
          new Error(
            "fetchSpanFields request timed out after 30s. Try fetching fewer spans or specific fields.",
          ),
        );
      }
    }, 30000);

    pendingRequests.set(requestId, { resolve, reject, timeoutId });

    postMessage({
      type: "fetch-span-fields",
      requestId,
      spanIds: uncached,
      fields,
    });
  }).then((data: Record<string, SpanFields>) => {
    for (const [id, fieldData] of Object.entries(data)) {
      if (!spanFieldsCache.has(id)) {
        spanFieldsCache.set(id, new Map());
      }
      spanFieldsCache.get(id)?.set(cacheKey, fieldData);
    }
    return Object.fromEntries(
      ids.map((id) => {
        const cached = spanFieldsCache.get(id)?.get(cacheKey);
        return [id, cached ?? data[id]];
      }),
    );
  });
}

function ErrorDisplay({ error, stack }: { error: string; stack?: string }) {
  return (
    <div className="error-container">
      <div className="error-title">Renderer Error</div>
      <div className="error-message">{error}</div>
      {stack && (
        <details style={{ marginTop: "1rem" }}>
          <summary style={{ cursor: "pointer" }}>Stack Trace</summary>
          <pre style={{ fontSize: "0.75rem", marginTop: "0.5rem" }}>
            {stack}
          </pre>
        </details>
      )}
    </div>
  );
}

function LoadingDisplay() {
  return <div className="loading-container">Rendering...</div>;
}

function WaitingDisplay() {
  return <div className="loading-container">Waiting for renderer code...</div>;
}

function showError(error: string, stack?: string): void {
  root.render(<ErrorDisplay error={error} stack={stack} />);
  postMessage({ type: "error", error, stack });
}

function showLoading(): void {
  root.render(<LoadingDisplay />);
}

function getSelectedSpan(): { id: string; span: CustomRendererSpan } | null {
  if (state.trace) {
    const selectedSpanId = state.trace.selectedSpanId || state.span?.span_id;
    if (selectedSpanId && state.trace.spans[selectedSpanId]) {
      return { id: selectedSpanId, span: state.trace.spans[selectedSpanId] };
    }
  }

  if (state.span) {
    return { id: state.span.span_id, span: state.span };
  }

  return null;
}

function resolveSpanFromId(
  id: string,
): { resolvedSpanId: string; span: CustomRendererSpan } | null {
  const trace = state.trace;
  if (!trace) {
    return null;
  }

  const spanById = trace.spans[id];
  if (spanById) {
    return { resolvedSpanId: id, span: spanById };
  }

  const spans = Object.values(trace.spans);

  const spanByRowId = spans.find((span) => span.id === id);
  if (spanByRowId) {
    return { resolvedSpanId: spanByRowId.span_id, span: spanByRowId };
  }

  const spanIdPrefixMatches = Object.keys(trace.spans).filter((spanId) =>
    spanId.startsWith(id),
  );
  if (spanIdPrefixMatches.length === 1) {
    const resolvedSpanId = spanIdPrefixMatches[0];
    const span = trace.spans[resolvedSpanId];
    if (span) {
      return { resolvedSpanId, span };
    }
  }

  const rowIdPrefixMatches = spans.filter((span) => span.id.startsWith(id));
  if (rowIdPrefixMatches.length === 1) {
    const span = rowIdPrefixMatches[0];
    return { resolvedSpanId: span.span_id, span };
  }

  return null;
}

function resolveTraceUpdateTarget(target?: CustomViewTraceUpdateTarget): {
  resolvedSpanId: string;
  span: CustomRendererSpan;
} {
  const resolvedTarget = target ?? "selected";
  if (resolvedTarget === "selected") {
    const selectedSpan = getSelectedSpan();
    if (!selectedSpan) {
      throw new Error("No span available to update");
    }
    return {
      resolvedSpanId: selectedSpan.id,
      span: selectedSpan.span,
    };
  }

  if (resolvedTarget === "root") {
    if (!state.trace) {
      throw new Error("No trace available to update");
    }
    const rootSpan = resolveSpanFromId(state.trace.rootSpanId);
    if (!rootSpan) {
      throw new Error("Root span not found in trace");
    }
    return rootSpan;
  }

  if (!state.trace) {
    throw new Error("No trace available to update");
  }

  const exactSpan = state.trace.spans[resolvedTarget.spanId];
  if (exactSpan) {
    return {
      resolvedSpanId: resolvedTarget.spanId,
      span: exactSpan,
    };
  }

  const exactRow = Object.values(state.trace.spans).find(
    (span) => span.id === resolvedTarget.spanId,
  );
  if (exactRow) {
    return {
      resolvedSpanId: exactRow.span_id,
      span: exactRow,
    };
  }

  throw new Error(`Span not found: ${resolvedTarget.spanId}`);
}

function applyLocalTraceUpdate(update: CustomViewTraceUpdate): void {
  const resolved = resolveTraceUpdateTarget(update.target);
  const nextData: CustomRendererSpan["data"] = {
    ...resolved.span.data,
  };

  if (hasOwn(update, "metadata")) {
    const nextMetadata = normalizeMetadata(nextData.metadata);
    Object.assign(nextMetadata, update.metadata ?? {});
    nextData.metadata = nextMetadata;
  }
  if (hasOwn(update, "scores")) {
    const nextScores = { ...(nextData.scores ?? {}) };
    Object.assign(nextScores, update.scores ?? {});
    nextData.scores = nextScores;
  }
  if (hasOwn(update, "tags")) {
    nextData.tags = update.tags ?? undefined;
  }

  const updatedSpan: CustomRendererSpan = {
    ...resolved.span,
    data: nextData,
  };
  clearCachedSpanFieldsForSpan(updatedSpan);

  if (state.trace && state.trace.spans[resolved.resolvedSpanId]) {
    state.trace = {
      ...state.trace,
      spans: {
        ...state.trace.spans,
        [resolved.resolvedSpanId]: updatedSpan,
      },
    };
  }

  if (state.span && state.span.span_id === resolved.resolvedSpanId) {
    state.span = updatedSpan;
  }

  if (state.component && state.trace && state.span) {
    renderRenderer();
  }
}

async function updateTrace(
  update: CustomViewTraceUpdate,
): Promise<{ transactionId: string | null }> {
  const target = update.target ?? "selected";
  const resolved = resolveTraceUpdateTarget(target);
  const traceRootSpanId = state.trace?.rootSpanId ?? resolved.span.root_span_id;
  const parentUpdate: CustomViewTraceUpdate = {
    ...update,
    target: { spanId: resolved.span.id },
  };

  const requestId = crypto.randomUUID();
  return new Promise<{ transactionId: string | null }>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (pendingUpdateRequests.has(requestId)) {
        pendingUpdateRequests.delete(requestId);
        reject(
          new Error(
            "trace.update request timed out after 30s. The Braintrust parent frame may be unavailable.",
          ),
        );
      }
    }, 30000);

    pendingUpdateRequests.set(requestId, { resolve, reject, timeoutId });

    postMessage({
      type: "update-span",
      requestId,
      update: parentUpdate,
    });
  }).then((data) => {
    const currentTarget = resolveSpanFromId(resolved.span.id);
    if (
      state.trace?.rootSpanId === traceRootSpanId &&
      currentTarget?.span.id === resolved.span.id
    ) {
      applyLocalTraceUpdate(parentUpdate);
    }
    return data;
  });
}

function applyLocalDatasetUpdate(update: CustomViewDatasetUpdate): void {
  if (!state.data) {
    return;
  }

  const nextData: Record<string, unknown> = {
    ...state.data,
  };
  if (hasOwn(update, "metadata")) {
    const nextMetadata = normalizeMetadata(nextData.metadata);
    Object.assign(nextMetadata, update.metadata ?? {});
    nextData.metadata = nextMetadata;
  }
  if (hasOwn(update, "input")) {
    nextData.input = update.input;
  }
  if (hasOwn(update, "expected")) {
    nextData.expected = update.expected;
  }
  if (hasOwn(update, "tags")) {
    nextData.tags = update.tags ?? undefined;
  }

  state.data = nextData;

  if (state.component && state.data) {
    renderRenderer();
  }
}

async function updateDatasetRow(
  update: CustomViewDatasetUpdate,
): Promise<{ transactionId: string | null }> {
  const rowAtRequest = state.data;
  const rowIdAtRequest = getRowId(rowAtRequest);
  const requestId = crypto.randomUUID();
  return new Promise<{ transactionId: string | null }>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (pendingUpdateRequests.has(requestId)) {
        pendingUpdateRequests.delete(requestId);
        reject(
          new Error(
            "update request timed out after 30s. Braintrust may be unavailable.",
          ),
        );
      }
    }, 30000);

    pendingUpdateRequests.set(requestId, { resolve, reject, timeoutId });

    postMessage({
      type: "update-row",
      requestId,
      update,
    });
  }).then((data) => {
    const currentRowId = getRowId(state.data);
    const shouldApplyLocalUpdate = rowIdAtRequest
      ? currentRowId === rowIdAtRequest
      : state.data === rowAtRequest;
    if (shouldApplyLocalUpdate) {
      applyLocalDatasetUpdate(update);
    }
    return data;
  });
}

function renderRenderer(): void {
  if (!state.component) {
    showError("No component loaded");
    return;
  }

  if (!isTraceSandboxState(state) && !isGenericSandboxState(state)) {
    showError("Data unavailable");
    return;
  }

  try {
    if (isTraceSandboxState(state)) {
      const traceWithFetch: CustomRendererFullTrace = {
        ...state.trace,
        fetchSpanFields: async (spanIds, fields) => {
          const ids = Array.isArray(spanIds) ? spanIds : [spanIds];

          const rowIdToRequestedIds = new Map<string, string[]>();
          const rowIds: string[] = [];
          for (const requestedId of ids) {
            const resolved = resolveSpanFromId(requestedId);
            if (!resolved) {
              throw new Error(`Span not found: ${requestedId}`);
            }

            rowIds.push(resolved.span.id);
            const existing = rowIdToRequestedIds.get(resolved.span.id);
            if (existing) {
              existing.push(requestedId);
            } else {
              rowIdToRequestedIds.set(resolved.span.id, [requestedId]);
            }
          }

          const result = await fetchSpanFields([...new Set(rowIds)], fields);
          const translated: Record<string, SpanFields> = {};
          for (const [rowId, data] of Object.entries(result)) {
            const requestedIds = rowIdToRequestedIds.get(rowId);
            if (!requestedIds) {
              continue;
            }
            for (const requestedId of requestedIds) {
              translated[requestedId] = data;
            }
          }
          return translated;
        },
        update: updateTrace,
      };

      const props: CustomTraceRendererProps = {
        trace: traceWithFetch,
        span: state.span,
        update,
        selectSpan,
      };
      root.render(React.createElement(state.component, props));
    } else if (isGenericSandboxState(state)) {
      const props: GenericCustomViewProps = {
        ...state.data,
        update: updateDatasetRow,
      };
      root.render(React.createElement(state.component, props));
    } else {
      showError("Data unavailable");
      return;
    }
  } catch (error: unknown) {
    if (isError(error)) {
      showError(error.message, error.stack);
    } else {
      showError(String(error));
    }
  }
}

function update(field: string, value: unknown): void {
  const selectedSpan = getSelectedSpan();
  if (!selectedSpan) {
    showError("No span available to update metadata");
    return;
  }

  const nextMetadata = normalizeMetadata(selectedSpan.span.data.metadata);
  nextMetadata[field] = value;

  const updatedSpan: CustomRendererSpan = {
    ...selectedSpan.span,
    data: {
      ...selectedSpan.span.data,
      metadata: nextMetadata,
    },
  };

  if (state.trace && state.trace.spans[selectedSpan.id]) {
    state.trace = {
      ...state.trace,
      spans: {
        ...state.trace.spans,
        [selectedSpan.id]: updatedSpan,
      },
    };
  }

  if (state.span && state.span.span_id === selectedSpan.id) {
    state.span = updatedSpan;
  }

  if (state.component && state.trace && state.span) {
    renderRenderer();
  }

  postMessage({
    type: "update-metadata",
    field,
    value,
  });
}

function selectSpan(spanId: string): void {
  if (!state.trace) {
    showError("No trace available for span navigation");
    return;
  }

  const resolved = resolveSpanFromId(spanId);
  if (!resolved) {
    showError(`Span "${spanId}" not found in trace`);
    return;
  }

  postMessage({
    type: "select-span",
    spanId: resolved.resolvedSpanId,
  });
}

// TODO: Share these helpers with app/utils/custom-view/compile.ts via a shared workspace package
// For now, duplicated to avoid cross-bundle import complexity (sandbox uses Vite, app uses Next.js)
function stripImports(source: string): string {
  return source
    .replace(/import\s+.*?from\s+['"][^'"]*['"];?\s*/g, "")
    .replace(/import\s*{[^}]+}\s*from\s+['"][^'"]*['"];?\s*/g, "")
    .replace(/import\s+\*\s+as\s+\w+\s+from\s+['"][^'"]*['"];?\s*/g, "")
    .replace(
      /(?:const|let|var)\s+(?:\w+|\{[^}]+\})\s*=\s*require\s*\([^)]+\);?\s*/g,
      "",
    )
    .replace(/require\s*\([^)]+\);?\s*/g, "");
}

function normalizeExports(code: string): string {
  let exportedFunctionName: string | null = null;

  let updatedCode = code.replace(
    /export\s+default\s+function\s+(\w+)/g,
    (_match, name: string) => {
      exportedFunctionName = name;
      return `function ${name}`;
    },
  );

  updatedCode = updatedCode.replace(
    /export\s+default\s+(\w+)/g,
    (_match, name: string) => {
      exportedFunctionName = name;
      return name;
    },
  );

  updatedCode = updatedCode
    .replace(/export\s+function\s+(\w+)/g, "function $1")
    .replace(/export\s+const\s+(\w+)/g, "const $1")
    .replace(/export\s+let\s+(\w+)/g, "let $1")
    .replace(/export\s+var\s+(\w+)/g, "var $1");

  if (exportedFunctionName && !updatedCode.includes("module.exports")) {
    updatedCode = `${updatedCode.trim()}\n\nmodule.exports = ${exportedFunctionName};`;
  }

  return updatedCode;
}

function isReactComponent(
  value: unknown,
): value is
  | React.ComponentType<CustomTraceRendererProps>
  | React.ComponentType<GenericCustomViewProps> {
  return typeof value === "function";
}

function compileRenderer(
  code: string,
):
  | React.ComponentType<CustomTraceRendererProps>
  | React.ComponentType<GenericCustomViewProps> {
  const preparedCode = normalizeExports(stripImports(code));
  const transformedCode = transform(preparedCode, {
    presets: ["react"],
    filename: "CustomRenderer.jsx",
  }).code;

  if (!transformedCode) {
    throw new Error("Babel transformation returned empty code");
  }

  const componentModule: { exports: unknown } = { exports: {} };
  const moduleWrapper = new Function(
    "module",
    "exports",
    "React",
    transformedCode,
  );
  moduleWrapper(componentModule, componentModule.exports, React);

  const exported =
    isRecord(componentModule.exports) && componentModule.exports.default
      ? componentModule.exports.default
      : isRecord(componentModule.exports) &&
          componentModule.exports.CustomTraceRenderer
        ? componentModule.exports.CustomTraceRenderer
        : componentModule.exports;

  if (!isReactComponent(exported)) {
    throw new Error(
      'The renderer code must export a React component. Use "module.exports = YourComponent;" at the end of your code.',
    );
  }

  return exported;
}

function loadRenderer(
  code: string,
  messageData: Record<string, unknown>,
): void {
  showLoading();
  state.code = code;
  clearSpanFieldsCache();
  clearPendingRequests();

  try {
    state.component = compileRenderer(code);
  } catch (error) {
    if (isError(error)) {
      showError(error.message, error.stack);
    } else {
      showError(String(error));
    }
    return;
  }

  const { trace, span, code: _code, type: _type, ...genericData } = messageData;

  if (trace && span && typeof trace === "object" && typeof span === "object") {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- parent validates via Zod schema; full re-validation in sandbox is redundant
    state.trace = trace as Omit<
      CustomRendererFullTrace,
      "fetchSpanFields" | "update"
    >;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- parent validates via Zod schema
    state.span = span as CustomRendererSpan;
    state.data = null;
  } else {
    const previousRowId = getRowId(state.data);
    const nextRowId = getRowId(genericData);
    if (previousRowId && nextRowId && previousRowId !== nextRowId) {
      clearPendingRequests();
    }
    state.trace = null;
    state.span = null;
    state.data = genericData;
  }

  renderRenderer();
}

function syncData(messageData: Record<string, unknown>) {
  const { trace, span, type: _type, ...genericData } = messageData;

  if (trace && span && typeof trace === "object" && typeof span === "object") {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- parent validates via Zod schema
    const newTrace = trace as Omit<
      CustomRendererFullTrace,
      "fetchSpanFields" | "update"
    >;
    if (state.trace?.rootSpanId !== newTrace.rootSpanId) {
      clearSpanFieldsCache();
      clearPendingRequests();
    }
    state.trace = newTrace;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- parent validates via Zod schema
    state.span = span as CustomRendererSpan;
    state.data = null;
  } else {
    const previousRowId = getRowId(state.data);
    const nextRowId = getRowId(genericData);
    if (previousRowId && nextRowId && previousRowId !== nextRowId) {
      clearPendingRequests();
    }
    state.trace = null;
    state.span = null;
    state.data = genericData;
  }

  if (state.component) {
    renderRenderer();
  }
}

function handleUpdateCode(
  message: Extract<ParentMessage, { type: "update-code" }>,
): void {
  const { code, type: _type, ...messageData } = message;

  if (code !== state.code) {
    loadRenderer(code, messageData);
    return;
  }

  syncData(messageData);
}

window.addEventListener("message", (event: MessageEvent<ParentMessage>) => {
  const message = event.data;

  if (!message || typeof message !== "object") {
    return;
  }

  if (!trustedParentOrigin) {
    // Only allow load-code to establish trust; reject all other messages
    if (message.type !== "load-code") {
      return;
    }
    trustedParentOrigin = event.origin;
  } else if (event.origin !== trustedParentOrigin) {
    return;
  }

  switch (message.type) {
    case "load-code": {
      const { code, type: _type, ...messageData } = message;
      loadRenderer(code, messageData);
      break;
    }
    case "data": {
      const { type: _type, ...messageData } = message;
      syncData(messageData);
      break;
    }
    case "update-code":
      handleUpdateCode(message);
      break;
    case "ping":
      postMessage({
        type: "pong",
        version: window.SANDBOX_VERSION,
        buildTime: window.SANDBOX_BUILD_TIME,
      });
      break;
    case "span-fields-response": {
      const pending = pendingRequests.get(message.requestId);
      if (pending) {
        clearTimeout(pending.timeoutId);
        pendingRequests.delete(message.requestId);
        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.data);
        }
      }
      break;
    }
    case "update-span-response":
    case "update-row-response": {
      const pending = pendingUpdateRequests.get(message.requestId);
      if (pending) {
        clearTimeout(pending.timeoutId);
        pendingUpdateRequests.delete(message.requestId);
        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.data ?? { transactionId: null });
        }
      }
      break;
    }
  }
});

window.addEventListener("error", (event: ErrorEvent) => {
  showError(event.error?.message || event.message, event.error?.stack);
  event.preventDefault();
});

window.addEventListener(
  "unhandledrejection",
  (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (reason instanceof CustomViewUpdateCancelledError) {
      event.preventDefault();
      return;
    }

    if (isError(reason)) {
      showError(reason.message, reason.stack);
    } else {
      showError(String(reason));
    }
    event.preventDefault();
  },
);

postMessage({ type: "ready" });
root.render(<WaitingDisplay />);
